import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Popover,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Typography,
  useTheme,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useTranslation } from "react-i18next";

export interface ClientFilterOption {
  id: string;
  label: string;
  // Second line shown under the label in the list (e.g. legal name).
  sublabel?: string;
}

interface ClientFilterSelectorProps {
  options: ClientFilterOption[];
  value: string; // selected id, "" means "all"
  onChange: (id: string) => void;
  // Label for the "no filter" entry, e.g. "Todos" / "Todas" (gendered per page).
  allLabel: string;
  disabled?: boolean;
}

// Searchable client/company filter — same Popover-based UI as the OC
// builder's client picker (live search over label + sublabel, "Por
// defecto"/"A→Z" sort toggle), adapted for history filters: instead of a
// "+ Nuevo cliente" action it opens with an "all" entry that clears the
// filter.
const ClientFilterSelector: React.FC<ClientFilterSelectorProps> = ({
  options,
  value,
  onChange,
  allLabel,
  disabled,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [search, setSearch] = useState("");
  const [sortAlpha, setSortAlpha] = useState(true);

  const open = Boolean(anchorEl);

  const selected = useMemo(
    () => options.find((o) => o.id === value) || null,
    [options, value]
  );

  const filteredOptions = useMemo(() => {
    let list = [...options];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (o) =>
          o.label.toLowerCase().includes(q) ||
          (o.sublabel || "").toLowerCase().includes(q)
      );
    }
    if (sortAlpha) {
      list.sort((a, b) => a.label.localeCompare(b.label, "es"));
    }
    return list;
  }, [options, search, sortAlpha]);

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
    setSearch("");
  };
  const handleClose = () => {
    setAnchorEl(null);
    setSearch("");
  };

  const displayText = selected ? selected.label : allLabel;

  return (
    <Box>
      <Button
        variant="outlined"
        fullWidth
        disabled={disabled}
        onClick={handleOpen}
        endIcon={<KeyboardArrowDownIcon />}
        sx={{
          justifyContent: "space-between",
          textTransform: "none",
          color: selected ? "inherit" : theme.palette.neutral["600"],
          borderColor: theme.palette.neutral["300"],
        }}
      >
        <Typography
          variant="pSmall"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textAlign: "left",
          }}
        >
          {displayText}
        </Typography>
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: { sx: { width: anchorEl?.clientWidth, mt: 0.5 } },
        }}
      >
        <Box
          sx={{
            p: 1.5,
            borderBottom: `1px solid ${theme.palette.neutral["200"]}`,
          }}
        >
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder={t(
              "common.clientFilter.searchPlaceholder",
              "Search client…"
            )}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Box>

        <Box
          sx={{
            px: 1.5,
            py: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            borderBottom: `1px solid ${theme.palette.neutral["200"]}`,
          }}
        >
          <Typography variant="pSmallest" color={theme.palette.neutral["600"]}>
            {t("common.clientFilter.sort", "Sort:")}
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={sortAlpha ? "alpha" : "default"}
            onChange={(_e, v) => {
              if (v !== null) setSortAlpha(v === "alpha");
            }}
          >
            <ToggleButton value="default" sx={{ textTransform: "none" }}>
              {t("common.clientFilter.sortDefault", "Default")}
            </ToggleButton>
            <ToggleButton value="alpha" sx={{ textTransform: "none" }}>
              {t("common.clientFilter.sortAlpha", "A → Z")}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <ListItemButton
          selected={!value}
          onClick={() => {
            onChange("");
            handleClose();
          }}
        >
          <ListItemText primary={allLabel} />
        </ListItemButton>
        <Divider />

        <List sx={{ maxHeight: "34rem", overflowY: "auto", py: 0 }}>
          {filteredOptions.map((o) => (
            <ListItemButton
              key={o.id}
              selected={o.id === value}
              onClick={() => {
                onChange(o.id);
                handleClose();
              }}
            >
              <ListItemText primary={o.label} secondary={o.sublabel} />
            </ListItemButton>
          ))}
          {filteredOptions.length === 0 && (
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="pSmall" color={theme.palette.neutral["600"]}>
                {t("common.clientFilter.noResults", "No results")}
              </Typography>
            </Box>
          )}
        </List>
      </Popover>
    </Box>
  );
};

export default ClientFilterSelector;
